/** Every request body the governance API accepts. The screens use the same rules. */
import { z } from "zod";
import { isISODate } from "./dates";

const isoDate = z.string().refine(isISODate, "Use a real date (YYYY-MM-DD).");
/** Pence, as an integer. Up to £100m either way. */
const pence = z.number().int().min(-10_000_000_000).max(10_000_000_000);

const scheduleFields = {
  date: isoDate,
  repeat: z.enum(["week", "month", "quarter", "year"]).nullable(),
  until: isoDate.nullable(),
};

const untilAfterDate = <T extends { date: string; repeat: string | null; until: string | null }>(s: T, ctx: z.RefinementCtx) => {
  if (s.until && s.until < s.date) ctx.addIssue({ code: "custom", path: ["until"], message: "The last date can't be before the first." });
};

export const commitmentScheduleSchema = z
  .object({ ...scheduleFields, amount: pence.refine((v) => v > 0, "Enter an amount above £0.") })
  .superRefine(untilAfterDate);

export const commitmentSchema = z
  .object({
    name: z.string().trim().min(1, "Give it a name.").max(200),
    direction: z.enum(["in", "out"]),
    categoryId: z.string().nullable(),
    basis: z.enum(["contracted", "expected"]).default("contracted"),
    note: z.string().trim().max(2000).default(""),
    locked: z.boolean().default(false),
    dateAssumed: z.boolean().default(false),
    xeroContactId: z.string().trim().toLowerCase().max(100).nullable().default(null),
    connectedDirectorId: z.string().uuid().nullable().default(null),
    schedule: z.array(commitmentScheduleSchema).max(200),
  })
  .superRefine((c, ctx) => {
    // a line covered by Xero may have no schedule of its own yet: Xero's bills are its dates
    if (!c.schedule.length && !c.xeroContactId) ctx.addIssue({ code: "custom", path: ["schedule"], message: "Add at least one date and amount." });
  })
  .transform((c) => (c.direction === "out" ? { ...c, basis: "contracted" as const } : c));
export type CommitmentBody = z.input<typeof commitmentSchema>;

export const leverEntrySchema = z
  .object({
    ...scheduleFields,
    amount: pence.refine((v) => v !== 0, "Enter an amount."),
    commitmentId: z.string().nullable().default(null),
    basis: z.enum(["contracted", "expected"]).default("contracted"),
    note: z.string().trim().max(500).default(""),
  })
  .superRefine(untilAfterDate);

export const leverSchema = z.object({
  title: z.string().trim().min(1, "Give it a title.").max(200),
  kindId: z.string().nullable().default(null),
  connectedDirectorId: z.string().uuid().nullable().default(null),
  narrative: z.string().trim().max(20_000).default(""),
  entries: z.array(leverEntrySchema).max(200).default([]),
});
export type LeverBody = z.input<typeof leverSchema>;

export const leverActionSchema = z.object({
  action: z.enum(["propose", "withdraw", "approve", "reject", "reopen"]),
  note: z.string().trim().max(2000).optional(),
});
export type LeverAction = z.infer<typeof leverActionSchema>["action"];

export const leverApplySchema = z.object({ applied: z.boolean() });

export const balanceSchema = z.object({
  asAt: isoDate,
  amount: pence,
  note: z.string().trim().max(500).default(""),
});

export const thresholdsSchema = z
  .object({
    effectiveFrom: isoDate,
    green: pence,
    amber: pence,
    red: pence,
    note: z.string().trim().max(2000).default(""),
  })
  .superRefine((t, ctx) => {
    if (!(t.green > t.amber && t.amber > t.red)) ctx.addIssue({ code: "custom", path: ["green"], message: "Green must be above amber, and amber above red." });
  });

export const settingsSchema = z
  .object({
    yearStartMonth: z.number().int().min(1).max(12),
    gateWeeks: z.number().int().min(1).max(52),
    runOutWeeks: z.number().int().min(4).max(104),
    chartWeeks: z.number().int().min(4).max(52),
    salesVatPct: z.number().min(0).max(50),
    paymentTermsDays: z.number().int().min(0).max(180),
    runRateMonths: z.number().int().min(1).max(24),
    runRateExcluded: z.array(z.string().max(100)).max(200),
    bankAccountIds: z.array(z.string().max(100)).max(50),
  })
  .superRefine((s, ctx) => {
    if (s.gateWeeks > s.runOutWeeks) ctx.addIssue({ code: "custom", path: ["gateWeeks"], message: "The gate can't read further ahead than the run-out looks." });
    if (s.chartWeeks > s.runOutWeeks) ctx.addIssue({ code: "custom", path: ["chartWeeks"], message: "The chart can't show further ahead than the run-out looks." });
  });

export const listItemsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1).max(100),
        active: z.boolean().default(true),
        direction: z.enum(["in", "out"]).optional(),
      }),
    )
    .max(100),
});

export const userSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(120),
  role: z.enum(["director", "team", "observer"]),
  isAdmin: z.boolean().default(false),
});

export const userPatchSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: z.enum(["director", "team", "observer"]).optional(),
  isAdmin: z.boolean().optional(),
  active: z.boolean().optional(),
});

export const snapshotSchema = z.object({
  name: z.string().trim().min(1, "Give it a name.").max(200),
  note: z.string().trim().max(5000).default(""),
  view: z.enum(["applied", "approved"]).default("applied"),
});
export type SnapshotBody = z.input<typeof snapshotSchema>;

export const snapshotPatchSchema = z.object({
  name: z.string().trim().min(1, "Give it a name.").max(200).optional(),
  note: z.string().trim().max(5000).optional(),
});

const gateState = z.enum(["green", "amber", "red", "stop"]);

export const authorityRulesSchema = z.object({
  rules: z
    .array(
      z
        .object({
          id: z.string().optional(),
          label: z.string().trim().min(1, "Give each rule a label.").max(300),
          typeIds: z.array(z.string().uuid()).max(50).default([]),
          gateStates: z.array(gateState).max(4).default([]),
          over: pence.min(0).nullable().default(null),
          upTo: pence.min(0).nullable().default(null),
          relatedParty: z.enum(["any", "yes", "no"]).default("any"),
          approverIds: z.array(z.string().uuid()).max(20).default([]),
          mode: z.enum(["any", "majority", "all"]).default("any"),
          active: z.boolean().default(true),
        })
        .superRefine((r, ctx) => {
          if (r.over !== null && r.upTo !== null && r.upTo <= r.over) ctx.addIssue({ code: "custom", path: ["upTo"], message: "“Up to” must be above “over”." });
        }),
    )
    .max(50),
});
export type AuthorityRulesBody = z.input<typeof authorityRulesSchema>;

/* ------------------------------------------------------------------ */
/* Decisions and actions                                               */
/* ------------------------------------------------------------------ */

const uuid = z.string().uuid("Not a valid id.");
const optionalText = (max: number) => z.string().trim().max(max).default("");
const link = z
  .string()
  .trim()
  .max(2000)
  .refine((v) => /^https?:\/\/\S+$/i.test(v), "Use a full web address (https://…).");

export const decisionSchema = z.object({
  typeId: uuid,
  title: z.string().trim().min(1, "Give it a title.").max(200),
  wording: optionalText(20_000),
  rationale: optionalText(20_000),
  amount: pence.min(0).nullable().default(null),
  connectedDirectorId: uuid.nullable().default(null),
  supersedesId: uuid.nullable().default(null),
  reviewBy: isoDate.nullable().default(null),
  finalAfter: isoDate.nullable().default(null),
});
export type DecisionBody = z.input<typeof decisionSchema>;

export const decisionActionSchema = z.object({
  action: z.enum(["propose", "withdraw", "approve", "reject", "revoke"]),
  note: z.string().trim().max(2000).optional(),
});
export type DecisionAction = z.infer<typeof decisionActionSchema>["action"];

/** Recording a decision already taken (at a meeting, say): who decided, and when. */
export const decisionRecordSchema = z.object({
  deciderIds: z.array(uuid).min(1, "Pick who decided.").max(20),
  takenOn: isoDate,
  note: z.string().trim().max(2000).optional(),
});

/** A taken decision's dates, changed or reviewed. */
export const decisionDatesSchema = z.object({
  reviewBy: isoDate.nullable(),
  finalAfter: isoDate.nullable(),
  /** Mark it reviewed now. */
  reviewed: z.boolean().default(false),
  note: z.string().trim().max(2000).optional(),
});

export const consentRecordSchema = z.object({
  partyId: uuid,
  given: z.boolean(),
  date: isoDate,
  documentUrl: link.nullable().default(null),
  note: z.string().trim().max(2000).nullable().default(null),
});

const actionSource = z.object({
  kind: z.enum(["decision", "lever", "billing", "risk", "obligation", "release", "meeting"]),
  id: z.string().trim().min(1).max(200),
  label: z.string().trim().min(1).max(300),
});

const dueFields = {
  due: isoDate.nullable().default(null),
  milestoneId: uuid.nullable().default(null),
};
const oneDue = <T extends { due: string | null; milestoneId: string | null }>(a: T, ctx: z.RefinementCtx) => {
  if (a.due && a.milestoneId) ctx.addIssue({ code: "custom", path: ["due"], message: "Pick a date or a milestone, not both." });
};

export const actionSchema = z
  .object({
    title: z.string().trim().min(1, "Say what needs doing.").max(300),
    notes: optionalText(5000),
    ownerId: uuid.nullable().default(null),
    ...dueFields,
    source: actionSource.nullable().default(null),
  })
  .superRefine(oneDue);
export type ActionBody = z.input<typeof actionSchema>;

/** A change to an action. Moving a due date that was set needs a reason. */
export const actionUpdateSchema = z
  .object({
    title: z.string().trim().min(1, "Say what needs doing.").max(300),
    notes: optionalText(5000),
    ownerId: uuid.nullable().default(null),
    ...dueFields,
    reason: z.string().trim().max(500).optional(),
  })
  .superRefine(oneDue);
export type ActionUpdateBody = z.input<typeof actionUpdateSchema>;

export const actionCloseSchema = z.object({
  note: z.string().trim().max(2000).nullable().default(null),
  link: link.nullable().default(null),
});

export const actionReopenSchema = z.object({ reason: z.string().trim().min(1, "Say why it's open again.").max(500) });

export const actionBulkSchema = z
  .object({
    ids: z.array(uuid).min(1).max(200),
    ownerId: uuid.nullable().optional(),
    move: z.object({ ...dueFields, reason: z.string().trim().max(500).optional() }).superRefine(oneDue).optional(),
  })
  .superRefine((b, ctx) => {
    if (b.ownerId === undefined && !b.move) ctx.addIssue({ code: "custom", path: ["ids"], message: "Nothing to change." });
  });

export const decisionTypesSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each type a name.").max(100),
        consentGroupId: uuid.nullable().default(null),
        active: z.boolean().default(true),
      }),
    )
    .max(50),
});

export const consentGroupsSchema = z.object({
  groups: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each group a name.").max(100),
        comparator: z.enum(["over", "at_least"]).default("over"),
        pct: z.number().min(0).max(100),
        active: z.boolean().default(true),
        parties: z
          .array(
            z.object({
              id: z.string().optional(),
              name: z.string().trim().min(1, "Give each party a name.").max(200),
              weight: z.number().int().min(1, "Weight is at least 1.").max(1_000_000_000_000),
              active: z.boolean().default(true),
            }),
          )
          .max(200),
      }),
    )
    .max(20),
});

export const milestonesSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each milestone a name.").max(100),
        date: isoDate.nullable().default(null),
        active: z.boolean().default(true),
      }),
    )
    .max(100),
});

export const registerSettingsSchema = z.object({
  dueSoonDays: z.number().int().min(0).max(90),
  closeNeedsEvidence: z.boolean(),
});

/* ------------------------------------------------------------------ */
/* Risks                                                               */
/* ------------------------------------------------------------------ */

const level = z.number().int().min(1).max(5);
const rating = z.object({ l: level, i: level });

export const riskSchema = z
  .object({
    domainId: uuid,
    statement: z.string().trim().min(1, "Say what the risk is.").max(1000),
    notes: optionalText(10_000),
    ownerId: uuid.nullable().default(null),
    tierId: uuid,
    inherent: rating.nullable().default(null),
    residual: rating,
    target: rating.nullable().default(null),
    targetBy: isoDate.nullable().default(null),
    controls: z
      .array(
        z.object({
          id: z.string().max(100).optional(),
          what: z.string().trim().min(1, "Say what the control is.").max(1000),
          ownerId: uuid.nullable().default(null),
          effectiveness: z.enum(["working", "partly", "not_working", "untested"]).default("untested"),
        }),
      )
      .max(50)
      .default([]),
  })
  .superRefine((r, ctx) => {
    if (r.targetBy && !r.target) ctx.addIssue({ code: "custom", path: ["target"], message: "Rate the target to give it a date." });
  });
export type RiskBody = z.input<typeof riskSchema>;

export const riskReviewSchema = z.object({ ids: z.array(uuid).min(1).max(500), note: z.string().trim().max(1000).optional() });
export const riskCloseSchema = z.object({ note: z.string().trim().min(1, "Say why it's closed.").max(2000) });
export const riskIssueSchema = z.object({
  version: z.string().trim().min(1, "Give it a version.").max(20),
  note: z.string().trim().max(5000).default(""),
});

export const riskScaleSchema = z
  .object({
    likelihood: z.array(z.string().trim().min(1).max(40)).length(5),
    impact: z.array(z.string().trim().min(1).max(40)).length(5),
    bands: z
      .array(z.object({ name: z.string().trim().min(1).max(40), min: z.number().int().min(1).max(25) }))
      .min(2)
      .max(6),
    highLikelihoodFrom: level,
    highImpactFrom: level,
    zones: z.object({ accept: z.string().trim().min(1).max(40), manage: z.string().trim().min(1).max(40), prepare: z.string().trim().min(1).max(40), act: z.string().trim().min(1).max(40) }),
  })
  .superRefine((s, ctx) => {
    if (s.bands[0]!.min !== 1) ctx.addIssue({ code: "custom", path: ["bands", 0, "min"], message: "The first band starts at 1." });
    for (let k = 1; k < s.bands.length; k++) if (s.bands[k]!.min <= s.bands[k - 1]!.min) ctx.addIssue({ code: "custom", path: ["bands", k, "min"], message: "Each band starts above the one before." });
  });

export const riskDomainsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each area a name.").max(100),
        code: z
          .string()
          .trim()
          .toUpperCase()
          .regex(/^[A-Z]{1,6}$/, "Use 1 to 6 letters."),
        active: z.boolean().default(true),
      }),
    )
    .max(50)
    .superRefine((items, ctx) => {
      const seen = new Set<string>();
      items.forEach((d, k) => {
        if (seen.has(d.code)) ctx.addIssue({ code: "custom", path: [k, "code"], message: "Each area needs its own code." });
        seen.add(d.code);
      });
    }),
});

export const riskTiersSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each tier a name.").max(60),
        reviewMonths: z.number().int().min(1).max(36),
        active: z.boolean().default(true),
      }),
    )
    .min(1)
    .max(10),
});

/* ------------------------------------------------------------------ */
/* Obligations                                                         */
/* ------------------------------------------------------------------ */

const dateRule = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("once"), date: isoDate.nullable() }),
  z.object({ kind: z.literal("repeat"), every: z.number().int().min(1).max(60), unit: z.enum(["week", "month", "year"]), from: isoDate, basis: z.enum(["due", "done"]).default("due") }),
  z.object({
    kind: z.literal("after"),
    period: z.enum(["year_end", "vat_quarter", "paye_period", "incorporation"]),
    months: z.number().int().min(0).max(36),
    days: z.number().int().min(0).max(60),
    dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
    firstAccounts: z.boolean().optional(),
  }),
]);

export const obligationSchema = z.object({
  title: z.string().trim().min(1, "Say what's owed.").max(300),
  notes: optionalText(5000),
  categoryId: uuid.nullable().default(null),
  ownerId: uuid.nullable().default(null),
  leadDays: z.number().int().min(0).max(365).default(30),
  rule: dateRule,
  startsOn: isoDate,
  commitmentId: uuid.nullable().default(null),
  riskIds: z.array(uuid).max(20).default([]),
  active: z.boolean().default(true),
});
export type ObligationBody = z.input<typeof obligationSchema>;

export const obligationCompleteSchema = z
  .object({
    doneOn: isoDate,
    skipped: z.boolean().default(false),
    note: z.string().trim().max(2000).nullable().default(null),
    link: link.nullable().default(null),
  })
  .superRefine((c, ctx) => {
    if (!c.note && !c.link) ctx.addIssue({ code: "custom", path: ["note"], message: c.skipped ? "Say why it isn't needed." : "Add a note or a link to show it's done." });
  });

export const companyDatesSchema = z.object({
  incorporatedOn: isoDate.nullable(),
  vatQuarterEnds: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
  payeFrequency: z.enum(["monthly", "quarterly"]).nullable(),
});

export const obligationCategoriesSchema = z.object({
  items: z.array(z.object({ id: z.string().optional(), name: z.string().trim().min(1, "Give each category a name.").max(100), active: z.boolean().default(true) })).max(50),
});

/* ------------------------------------------------------------------ */
/* Release log                                                         */
/* ------------------------------------------------------------------ */

export const releaseSchema = z
  .object({
    frameworkId: uuid,
    what: z.string().trim().min(1, "Say what changed.").max(500),
    ownerId: uuid.nullable().default(null),
    entryType: z.enum(["recorded", "reconstructed"]).default("recorded"),
    status: z.enum(["scoped", "in_build", "in_review", "deployed", "withdrawn"]).default("scoped"),
    publishedOn: isoDate.nullable().default(null),
    detectedOn: isoDate.nullable().default(null),
    deployedOn: isoDate.nullable().default(null),
    releaseRef: z.string().trim().max(100).default(""),
    signOff: z.object({ reviewerId: uuid, signedOn: isoDate, evidenceUrl: link.nullable().default(null) }).nullable().default(null),
    withoutSignOff: z.string().trim().max(1000).nullable().default(null),
    riskIds: z.array(uuid).max(20).default([]),
    notes: optionalText(5000),
  })
  .superRefine((r, ctx) => {
    if (r.publishedOn && r.detectedOn && r.detectedOn < r.publishedOn) ctx.addIssue({ code: "custom", path: ["detectedOn"], message: "It can't be noticed before it was published." });
    if (r.detectedOn && r.deployedOn && r.deployedOn < r.detectedOn) ctx.addIssue({ code: "custom", path: ["deployedOn"], message: "It can't go live before it was noticed." });
    if (r.status === "deployed" && !r.deployedOn) ctx.addIssue({ code: "custom", path: ["deployedOn"], message: "Give the day it went live." });
    if (r.deployedOn && r.status !== "deployed") ctx.addIssue({ code: "custom", path: ["status"], message: "It has a live date: mark it live." });
    // the gate: signed off on or before the day it went live, or the reason it wasn't
    if (r.status === "deployed" && !(r.signOff && r.deployedOn && r.signOff.signedOn <= r.deployedOn) && !r.withoutSignOff)
      ctx.addIssue({ code: "custom", path: ["signOff"], message: "Record the sign-off from on or before the day it went live, or why it went live without one." });
  });
export type ReleaseBody = z.input<typeof releaseSchema>;

export const releaseFrameworksSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each framework a name.").max(150),
        detectDays: z.number().int().min(0).max(365).nullable(),
        deployDays: z.number().int().min(0).max(730).nullable(),
        active: z.boolean().default(true),
      }),
    )
    .max(50),
});

export const reviewersSchema = z.object({
  items: z.array(z.object({ id: z.string().optional(), name: z.string().trim().min(1, "Give each reviewer a name.").max(150), role: z.string().trim().max(150).default(""), active: z.boolean().default(true) })).max(50),
});

/* ------------------------------------------------------------------ */
/* Measures                                                            */
/* ------------------------------------------------------------------ */

const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use a month (YYYY-MM).");
const MEASURE_SOURCES = ["entered", "cash_month_end", "gate_low", "runway", "billed_unpaid", "mrr", "customers", "sites_billed", "sites_live", "demo_conversion", "retention_12m", "open_actions", "overdue_actions", "overdue_obligations", "high_risks", "changes_over_target"] as const;

export const measureDefsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1, "Give each measure a name.").max(150),
        unit: z.enum(["gbp", "percent", "number", "months", "days"]),
        headline: z.boolean().default(true),
        section: z.string().trim().max(100).default(""),
        ownerId: uuid.nullable().default(null),
        source: z.enum(MEASURE_SOURCES).default("entered"),
        liveFrom: month.nullable().default(null),
        standIn: z.string().trim().max(500).default(""),
        better: z.enum(["higher", "lower"]).default("higher"),
        active: z.boolean().default(true),
      }),
    )
    .max(60),
});

export const measureSettingsSchema = z
  .object({ onTrackPct: z.number().min(1).max(200), atRiskPct: z.number().min(0).max(200) })
  .superRefine((s, ctx) => {
    if (s.atRiskPct >= s.onTrackPct) ctx.addIssue({ code: "custom", path: ["atRiskPct"], message: "At risk starts below on track." });
  });

export const captureSchema = z.object({ month });
export const measureValueSchema = z.object({ value: z.number().finite().nullable() });
export const measureNoteSchema = z.object({ note: z.string().trim().max(1000) });
export const measureTargetsSchema = z.object({ targets: z.array(z.object({ month, value: z.number().finite().nullable() })).max(120) });
export const measureMonthParam = month;

/* ------------------------------------------------------------------ */
/* Monthly pack                                                        */
/* ------------------------------------------------------------------ */

const PANEL_KEYS = ["cash", "gate", "first_overdrawn", "sales_needed", "what_ifs", "related_party", "customer_cash", "paying", "objectives", "measures", "changes_open", "longest_to_live", "live_unsigned", "high_risks", "tier1_risks", "obligations", "actions", "actions_list", "decisions_waiting", "decisions_taken"] as const;

export const packSectionsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        title: z.string().trim().min(1, "Give each section a title.").max(150),
        ownerIds: z.array(uuid).max(10).default([]),
        ask: z.enum(["note", "approve", "discuss", "decide"]).nullable().default(null),
        fields: z
          .array(
            z.object({
              id: z.string().max(40).optional(),
              label: z.string().trim().min(1, "Give each field a label.").max(200),
              hint: z.string().trim().max(500).default(""),
              from: z.object({ sectionId: uuid, fieldId: z.string().min(1).max(40) }).nullable().default(null),
            }),
          )
          .min(1, "A section needs at least one field.")
          .max(20),
        panels: z.array(z.enum(PANEL_KEYS)).max(PANEL_KEYS.length).default([]),
        active: z.boolean().default(true),
      }),
    )
    .max(20),
});

export const packSettingsSchema = z.object({
  meetingWeekday: z.number().int().min(1).max(7),
  meetingNth: z.number().int().min(1).max(5),
  dueDaysBefore: z.number().int().min(0).max(31),
  issueWithGaps: z.boolean(),
  ownersApprove: z.boolean(),
  footer: z.string().trim().max(300),
  appendices: z.array(z.enum(["cash", "measures", "objectives", "risks", "releases"])).max(5),
  chartMonths: z.number().int().min(1).max(36),
  meetingStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time (14:00)."),
  meetingEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time (15:30)."),
  guestRoles: z.array(z.enum(["director", "team", "observer"])).max(3),
  planAhead: z.number().int().min(1).max(24),
  boardWeekday: z.number().int().min(1).max(7),
  boardNth: z.number().int().min(1).max(5),
  boardMonthsAfter: z.number().int().min(1).max(3),
  boardDueDaysBefore: z.number().int().min(0).max(31),
  chairId: uuid.nullable(),
  minutesById: uuid.nullable(),
  minutesDeputyId: uuid.nullable().default(null),
  minutesDrafterId: uuid.nullable().default(null),
  monthlyChairId: uuid.nullable().default(null),
  minutesDays: z.number().int().min(1).max(30).default(5),
}).refine((s) => s.meetingEnd > s.meetingStart, { path: ["meetingEnd"], message: "Ends after it starts." });

export const openPackSchema = z.object({ month, kind: z.enum(["monthly", "board"]).default("monthly") });
export const packDatesSchema = z
  .object({ meetingOn: isoDate, dueOn: isoDate })
  .refine((d) => d.dueOn <= d.meetingOn, { path: ["dueOn"], message: "Sections are due on or before the meeting." });
export const sectionFieldsSchema = z.object({ fields: z.record(z.string().max(40), z.string().max(20000)) });
export const returnSectionSchema = z.object({ note: z.string().trim().min(1, "Say what needs changing.").max(1000) });

/* ------------------------------------------------------------------ */
/* Meetings                                                            */
/* ------------------------------------------------------------------ */

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time (14:00).");
export const meetingSchema = z
  .object({
    kind: z.enum(["board", "other"]).default("other"),
    title: z.string().trim().min(1, "Give the meeting a title.").max(200),
    date: isoDate,
    start: hhmm.nullable().default(null),
    end: hhmm.nullable().default(null),
    guestRoles: z.array(z.enum(["director", "team", "observer"])).max(3).default([]),
    guestIds: z.array(uuid).max(50).default([]),
    guestEmails: z.array(z.string().trim().toLowerCase().email("Use an email address.")).max(50).default([]),
    notes: z.string().trim().max(2000).default(""),
  })
  .refine((m) => (m.start === null) === (m.end === null), { path: ["end"], message: "Give both times, or neither for all day." })
  .refine((m) => !m.start || !m.end || m.end > m.start, { path: ["end"], message: "Ends after it starts." });
export const meetingUpdateSchema = z.object({
  title: z.string().trim().min(1).max(200),
  date: isoDate,
  start: hhmm.nullable(),
  end: hhmm.nullable(),
  guestRoles: z.array(z.enum(["director", "team", "observer"])).max(3),
  guestIds: z.array(uuid).max(50),
  guestEmails: z.array(z.string().trim().toLowerCase().email("Use an email address.")).max(50),
  notes: z.string().trim().max(2000),
}).refine((m) => (m.start === null) === (m.end === null), { path: ["end"], message: "Give both times, or neither for all day." });
export const calendarRangeSchema = z.object({ from: isoDate, to: isoDate }).refine((r) => r.from <= r.to, { path: ["to"], message: "The range ends after it starts." });

/* ------------------------------------------------------------------ */
/* Reminders                                                           */
/* ------------------------------------------------------------------ */

const REMINDER_KIND_LIST = ["pack_section", "pack_returned", "pack_approval", "figure", "action", "action_check", "decision", "consent", "decision_review", "obligation", "release", "risk_review", "risk_escalation", "minutes", "minutes_sign", "minutes_circulate"] as const;
const dayCount = z.number().int().min(1).max(90);
export const reminderSettingsSchema = z
  .object({
    on: z.boolean(),
    sendFrom: hhmm,
    sendUntil: hhmm,
    rules: z
      .array(
        z.object({
          kind: z.enum(REMINDER_KIND_LIST),
          on: z.boolean(),
          lands: z.boolean(),
          before: z.union([dayCount, z.literal("lead")]).nullable(),
          onDay: z.boolean(),
          every: dayCount.nullable(),
          upTo: z.number().int().min(1).max(20).nullable(),
        }),
      )
      .max(REMINDER_KIND_LIST.length),
  })
  .refine((s) => s.sendUntil > s.sendFrom, { path: ["sendUntil"], message: "Ends after it starts." })
  .refine((s) => new Set(s.rules.map((r) => r.kind)).size === s.rules.length, { path: ["rules"], message: "One rule a kind." });

export const historyQuerySchema = z.object({
  actorId: uuid.optional(),
  area: z.enum(["finance", "decisions", "risks", "obligations", "releases", "measures", "packs", "meetings", "people", "settings", "other"]).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  q: z.string().trim().max(200).optional(),
  before: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(5000).default(100),
});

/* ------------------------------------------------------------------ */
/* Minutes                                                             */
/* ------------------------------------------------------------------ */

const minutesText = (max: number) => z.string().max(max).default("");
export const minutesBodySchema = z.object({
  chairId: uuid.nullable(),
  present: minutesText(2000),
  apologies: minutesText(2000),
  discussion: minutesText(60_000),
  decisions: minutesText(20_000),
  notesUrl: z.string().trim().url("Use the notes' link.").max(500).nullable().default(null),
});
export const minutesReturnSchema = z.object({ note: z.string().trim().min(1, "Say what needs changing.").max(2000) });

/* ------------------------------------------------------------------ */
/* Drive                                                               */
/* ------------------------------------------------------------------ */

const folderName = z.string().trim().min(1, "Name each folder.").max(100).regex(/^[^/\\]+$/, "No slashes in a folder name.");
export const driveSettingsSchema = z.object({
  driveId: z.string().trim().min(1).max(200).nullable(),
  driveName: z.string().trim().max(200).nullable(),
  prefix: z.string().trim().min(1, "Give the file names a prefix.").max(20).regex(/^[A-Za-z0-9-]+$/, "Letters, numbers and hyphens."),
  folders: z.object({ readMe: folderName, cycles: folderName, board: folderName, finance: folderName, registers: folderName, investors: folderName, company: folderName, framework: folderName, archive: folderName }),
  cycle: z.object({ sections: folderName, accounts: folderName, meeting: folderName, issued: folderName }),
  boardMeeting: folderName,
});
